import { z } from 'zod';
import { OpportunityStateSchema } from './enums.js';
import { TrackedSignalBookOddsSchema, TrackedSignalMatchSchema } from './signals.js';

// ─── Kelly stake recommendation (§ 9.4 V1 core) ────────────────────────────
//
// Computed on-read in apps/api/src/routes/opportunities.ts so changes to the
// operator's bankroll/Kelly fraction in settings reflect immediately on the
// dashboard. NOT persisted in the opportunities table for V1.

export const OpportunityStakeCappedBySchema = z.enum([
  'fraction',
  'drawdown_fraction',
  'max_stake',
  'min_stake',
  'no_edge',
  'no_bankroll',
]);
export type OpportunityStakeCappedBy = z.infer<typeof OpportunityStakeCappedBySchema>;

export const OpportunityStakeSchema = z.object({
  /** Final dollar amount to wager. 0 if any guard tripped. */
  suggestedStakeUsd: z.number().nonnegative(),
  /** Pure Kelly fraction (f*), pre-fraction adjustment, pre-cap. */
  kellyPctFull: z.number(),
  /** Final fraction of bankroll, post fraction-adjustment + cap. */
  kellyPctApplied: z.number(),
  /** Constraint that determined the final stake. */
  cappedBy: OpportunityStakeCappedBySchema,
  /** Bankroll snapshot used for the calculation (≥ 0). */
  bankrollUsd: z.number().nonnegative(),
  /** Kelly fraction actually applied (drawdown-aware). */
  kellyFractionUsed: z.number().nonnegative(),
  /** True if drawdown-mode fraction was used. */
  inDrawdown: z.boolean(),
});
export type OpportunityStake = z.infer<typeof OpportunityStakeSchema>;

// ─── opportunities row + observation row ───────────────────────────────────

export const OpportunityRowSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  modelSignalId: z.string().uuid(),
  canonicalMatchId: z.string().uuid(),
  market: z.string(),
  selection: z.string(),
  targetOdds: z.number(),
  state: OpportunityStateSchema,
  currentBestBook: z.string().nullable(),
  currentBestOdds: z.number().nullable(),
  currentEdgePct: z.number().nullable(),
  peakBook: z.string().nullable(),
  peakOdds: z.number().nullable(),
  peakObservedAt: z.string().datetime().nullable(),
  firstDetectedAt: z.string().datetime(),
  lastEvaluatedAt: z.string().datetime(),
  weakenedAt: z.string().datetime().nullable(),
  closedAt: z.string().datetime().nullable(),
  expiredAt: z.string().datetime().nullable(),
  snoozedUntil: z.string().datetime().nullable(),
  operatorActionAt: z.string().datetime().nullable(),
  operatorActionBy: z.string().uuid().nullable(),
  rankScore: z.number().nullable(),
  context: z.record(z.unknown()),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  // Embedded so the operator card can render player names + start time
  // without a second matches.search round-trip. Null when canonical_match_id
  // hasn't been linked yet (early seed before connector backfill).
  match: TrackedSignalMatchSchema.nullable(),
  // Kelly Engine stake recommendation. Null when the operator config can't
  // be loaded (rare — shouldn't happen for authenticated requests). Computed
  // on-read in the API: see kelly.ts in @tennis-scanner/decision.
  stake: OpportunityStakeSchema.nullable(),
  // Optional dollar stake the operator entered when creating the manual
  // pick (signal.metadata.manualStakeUsd). UI shows this next to Kelly so
  // the operator can compare their gut size vs the math. Null = not set.
  manualStakeUsd: z.number().nonnegative().nullable(),
  // Per-book odds for this pick across the FULL book universe (allowed +
  // reference). isAllowed flags which books generate opportunities. Used by
  // the dashboard card to show the complete market, not just allowed books.
  perBook: z.array(TrackedSignalBookOddsSchema),
});
export type OpportunityRow = z.infer<typeof OpportunityRowSchema>;

export const OpportunityObservationRowSchema = z.object({
  id: z.string().uuid(),
  opportunityId: z.string().uuid(),
  observedAt: z.string().datetime(),
  stateBefore: OpportunityStateSchema,
  stateAfter: OpportunityStateSchema,
  book: z.string().nullable(),
  odds: z.number().nullable(),
  edgePct: z.number().nullable(),
  reason: z.string(),
  meta: z.record(z.unknown()),
  idempotencyKey: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type OpportunityObservationRow = z.infer<typeof OpportunityObservationRowSchema>;

// ─── operator actions ──────────────────────────────────────────────────────

export const OpportunitySnoozeInputSchema = z.object({
  opportunityId: z.string().uuid(),
  untilMinutes: z
    .number()
    .int()
    .min(1)
    .max(60 * 24),
  reason: z.string().max(500).optional(),
  idempotencyKey: z.string().uuid(),
});
export type OpportunitySnoozeInput = z.infer<typeof OpportunitySnoozeInputSchema>;

export const OpportunityMarkPassedInputSchema = z.object({
  opportunityId: z.string().uuid(),
  reason: z.string().max(500).optional(),
  idempotencyKey: z.string().uuid(),
});
export type OpportunityMarkPassedInput = z.infer<typeof OpportunityMarkPassedInputSchema>;

export const OpportunityMarkExecutedInputSchema = z.object({
  opportunityId: z.string().uuid(),
  // The book where the operator actually placed the bet — may differ from
  // current_best_book (e.g. ops took a slightly worse price for liquidity).
  executedBook: z.string().min(1).max(64),
  executedOdds: z.number().gte(1.01).lte(1000),
  // Optional — operator may not always log stake (external bankroll users).
  executedStakeUsd: z.number().nonnegative().optional(),
  reason: z.string().max(500).optional(),
  idempotencyKey: z.string().uuid(),
});
export type OpportunityMarkExecutedInput = z.infer<typeof OpportunityMarkExecutedInputSchema>;

// ─── listing filter ────────────────────────────────────────────────────────

export const OPPORTUNITY_SORT = ['rankScore', 'currentEdgePct', 'firstDetectedAt'] as const;
export const OpportunityListFilterSchema = z.object({
  state: z.array(OpportunityStateSchema).optional(),
  matchId: z.string().uuid().optional(),
  sortBy: z.enum(OPPORTUNITY_SORT).default('rankScore'),
  limit: z.number().int().min(1).max(500).default(100),
});
export type OpportunityListFilter = z.infer<typeof OpportunityListFilterSchema>;
